<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.expenseTypes',
    path: 'expense-types',
    component: 'ExpenseTypes',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExpenseTypes',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const objectType = ref('expense-type');
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'code',
      header: $fn.value.gettext('Code'),
      width: '160px',
      required: true,
    },
    {
      name: 'name',
      header: $fn.value.gettext('Name'),
      width: '140px',
      required: true,
    },
    {
      name: 'description',
      header: $fn.value.gettext('Description'),
      width: '320px',
    },
  ]);

  const $data = computed(() => ({
    objectType: objectType.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: objectType.value },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectType' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      objectType,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_rp7j9hqc5_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_g3a8jgy9y_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .upsert({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_g3a8jgy9y_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_g3a8jgy9y_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_gycheg7en_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .delete({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_gycheg7en_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_gycheg7en_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_rp7j9hqc5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_rp7j9hqc5_getActionArgs,
    });
  const inst_evh_7315092382398562_close_y3rr5etpw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_rp7j9hqc5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_y3rr5etpw],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_a5eoo51uy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8c0c54r7q =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_a5eoo51uy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8c0c54r7q],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_umz8lk98r =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_5el23heap =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_umz8lk98r,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_5el23heap],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_g3a8jgy9y = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_g3a8jgy9y_getActionArgs,
  });
  const inst_evh_3343864469950044_save_a2ssux7nh =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_g3a8jgy9y,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_a2ssux7nh],
    event: 'save',
  });
  const act_3343864469950044_script_gycheg7en = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_gycheg7en_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_83jyzemkb =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_gycheg7en,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_83jyzemkb],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_y3rr5etpw,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8c0c54r7q,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_5el23heap,
    reload: inst_evh_8320016629450276_clickPreventStop_5el23heap,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_a2ssux7nh,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_83jyzemkb,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_y3rr5etpw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_y3rr5etpw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8c0c54r7q = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8c0c54r7q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_5el23heap = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_5el23heap.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_a2ssux7nh = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_a2ssux7nh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_83jyzemkb = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_83jyzemkb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ExpenseTypes"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ gettext('Expense types') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.ExpenseTypes"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
